// src/utils/analytics.js
// ✅ PostHog Analytics — traccia eventi utente per migliorare il prodotto
// GDPR compliant — nessun dato sensibile viene inviato
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

// ==================== INIT ====================
export const initAnalytics = () => {
    if (!POSTHOG_KEY) return; // Disabilitato se no chiave
    if (process.env.NODE_ENV === 'development') return; // Solo in produzione

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,       // Traccia navigazione automatica
        capture_pageleave: true,       // Traccia uscita pagine
        autocapture: false,      // Disabilitato — tracciamo solo eventi custom
        disable_session_recording: false, // Registra sessioni (vedi cosa fa l'utente)
        respect_dnt: true,       // Rispetta "Do Not Track"
        loaded: (ph) => {
            if (process.env.NODE_ENV === 'development') ph.opt_out_capturing();
        },
    });
};

// ==================== IDENTIFY UTENTE ====================
// Chiama dopo il login — associa eventi all'utente
export const identifyUser = (user, organization) => {
    if (!POSTHOG_KEY || !user) return;
    posthog.identify(user.id, {
        // ✅ Solo dati NON sensibili
        role: user.role,
        plan: organization?.plan,
        region: organization?.region,
        country: organization?.country,
        currency: organization?.currency,
        language: user.language,
        created_at: user.createdAt,
    });
    posthog.group('organization', organization?.id, {
        name: organization?.name,
        plan: organization?.plan,
        region: organization?.region,
        country: organization?.country,
    });
};

// ==================== RESET (logout) ====================
export const resetAnalytics = () => {
    if (!POSTHOG_KEY) return;
    posthog.reset();
};

// ==================== TRACK EVENTI ====================
// Chiama ovunque nell'app per tracciare eventi
export const track = (event, properties = {}) => {
    if (!POSTHOG_KEY) return;
    posthog.capture(event, {
        ...properties,
        timestamp: new Date().toISOString(),
    });
};

// ==================== EVENTI PREDEFINITI ====================
// Usa questi invece di stringhe libere — mantiene consistenza

export const Events = {
    // Auth
    LOGIN: 'user_logged_in',
    LOGOUT: 'user_logged_out',
    REGISTER: 'user_registered',
    PASSWORD_RESET: 'password_reset_requested',

    // Progetti
    PROJECT_CREATED: 'project_created',
    PROJECT_VIEWED: 'project_viewed',
    PROJECT_EDITED: 'project_edited',
    PROJECT_DELETED: 'project_deleted',
    PROJECT_STATUS_CHANGED: 'project_status_changed',

    // Spese
    EXPENSE_CREATED: 'expense_created',
    EXPENSE_VALIDATED: 'expense_validated',
    EXPENSE_DELETED: 'expense_deleted',
    EXPENSE_EXPORTED: 'expense_exported',

    // AI
    AI_CHAT_OPENED: 'ai_chat_opened',
    AI_MESSAGE_SENT: 'ai_message_sent',
    AI_PLANNER_OPENED: 'ai_planner_opened',
    AI_PLAN_GENERATED: 'ai_plan_generated',
    AI_PLAN_APPLIED: 'ai_plan_applied',
    AI_NO_MESSAGE: 'ai_opened_no_message',      // Apre AI ma non invia
    AI_QUOTA_HIT: 'ai_quota_exceeded',

    // Team
    TEAM_INVITE_SENT: 'team_invite_sent',
    TEAM_MEMBER_ADDED: 'team_member_added',
    CUSTOM_ROLE_CREATED: 'custom_role_created',

    // Report
    REPORT_GENERATED: 'report_generated',
    REPORT_DOWNLOADED: 'report_downloaded',

    // Upgrade
    UPGRADE_PAGE_VIEWED: 'upgrade_page_viewed',
    UPGRADE_CLICKED: 'upgrade_clicked',
    UPGRADE_COMPLETED: 'upgrade_completed',
    UPGRADE_ABANDONED: 'upgrade_abandoned',         // Esce dalla pagina upgrade

    // Onboarding
    ONBOARDING_TOUR_STARTED: 'onboarding_tour_started',
    ONBOARDING_TOUR_COMPLETED: 'onboarding_tour_completed',
    ONBOARDING_TOUR_SKIPPED: 'onboarding_tour_skipped',
    ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
    CHECKLIST_ITEM_COMPLETED: 'checklist_item_completed',

    // Navigazione
    PAGE_VIEWED: 'page_viewed',
    SEARCH_PERFORMED: 'search_performed',
    SEARCH_NO_RESULTS: 'search_no_results',         // Cerca e non trova

    // Errori
    ERROR_SHOWN: 'error_shown',
    FEATURE_BLOCKED: 'feature_blocked_by_plan',   // Funzione non disponibile nel piano
};

export default posthog;